// drag-scroll
import { onDestroy, onMount, onView } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { Resize } from "@lib/subs";
import { symmetricMod } from "@utils/math";
import { handleEditor } from "@webflow/detect-editor";

// Endless row that steps itself one card at a time and can be dragged. The
// active card rests centred at full size, neighbours shrink.
//
//   [data-module="drag-scroll"]     <- the row; clips, owns the gesture
//     <child>                       <- one card, xn
//
// Cards are the element's children. Tag them [drag-scroll="item"] only when
// they sit deeper (a Webflow collection puts them under .w-dyn-items).
//
//   data-loop-interval="3"    seconds of rest between steps (default 4)
//   data-loop-duration="0.9"  seconds one step takes
//   data-loop-scale="0.8"     card size at full distance (1 disables)
//   data-loop-reverse="true"  step the other way
//   data-loop-fill="false"    leave card heights alone
//
// Equal heights are flex's doing: the row is a flex line so items stretch to
// the tallest. fillTargets carries that down to the wrapper you can actually
// see. Where a card's slack lands is still the design's call (`flex: 1` on the
// child that should absorb it).

const DEFAULT_INTERVAL = 4;
const DEFAULT_STEP_DURATION = 1;
const DEFAULT_INACTIVE_SCALE = 0.9;
const SETTLE_DURATION = 0.7;
const EASE = "power3.out";

const THROW_PROJECTION = 0.15;
// Fraction of a card a flick must clear to advance rather than spring back.
const SNAP_THRESHOLD = 0.2;

const DRAG_THRESHOLD = 4;
// ~one frame at 60Hz, in time so a 120Hz display doesn't halve a flick.
const VELOCITY_EMA_MS = 33;

const VIEW_MARGIN = "20% 0px";
const FOCUSABLE = "a, button, input, select, textarea, [tabindex]";
const FILL_MAX_DEPTH = 4;

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const tagged = Array.from(
    element.querySelectorAll<HTMLElement>('[drag-scroll="item"]'),
  );
  const items = tagged.length
    ? tagged
    : (Array.from(element.children) as HTMLElement[]);
  if (items.length === 0) return;

  // Clones join the cards here, and everything is measured against it.
  const row: HTMLElement = items[0].parentElement ?? element;

  const interval = Number(dataset.loopInterval) || DEFAULT_INTERVAL;
  const stepDuration = Number(dataset.loopDuration) || DEFAULT_STEP_DURATION;
  const inactiveScale = Number(dataset.loopScale) || DEFAULT_INACTIVE_SCALE;
  const direction: -1 | 1 = dataset.loopReverse === "true" ? 1 : -1;

  // Absolutely positioned children are overlays: they take no part in the
  // parent's height, so counting them would stop the walk on a box that really
  // does have a single content child.
  function soleFlowChild(node: HTMLElement): HTMLElement | null {
    let found: HTMLElement | null = null;
    for (const child of Array.from(node.children) as HTMLElement[]) {
      const { position } = getComputedStyle(child);
      if (position === "absolute" || position === "fixed") continue;
      if (found) return null;
      found = child;
    }
    return found;
  }

  // Walk down while a box has one in-flow child, so the item's stretch reaches
  // the visible card. Two children would each take the full height and overflow.
  const fillTargets: HTMLElement[] = [];
  if (dataset.loopFill !== "false") {
    for (const item of items) {
      let node: HTMLElement | null = item;
      for (let depth = 0; depth < FILL_MAX_DEPTH; depth++) {
        node = soleFlowChild(node);
        if (!node) break;
        fillTargets.push(node);
      }
    }
  }

  // One cycle of the row, gaps included. All modular maths is in it.
  let loopWidth = 0;
  // Offset at which each card sits centred. Stored per card rather than
  // derived from one pitch, so cards of different widths each centre right.
  let anchors: number[] = [];
  // Layout position and width of every moving node, clones included.
  let bases: number[] = [];
  let widths: number[] = [];
  let viewportCenter = 0;
  // Distance over which a card shrinks all the way: one card of travel.
  let focusRange = 1;
  let index = 0;

  // Tweened directly; render() folds it back into one cycle.
  const pos = { x: 0 };

  const clones: HTMLElement[] = [];
  // Originals and clones share one offset, differing only in scale.
  let moving: HTMLElement[] = [];

  let tween: gsap.core.Tween | null = null;
  let timer: gsap.core.Tween | null = null;
  let inView = false;
  let destroyed = false;

  let pointerId: number | null = null;
  let dragging = false;
  // Until the axis resolves the row doesn't move and the pointer isn't
  // captured, so a swipe that turns out to be a page scroll is never stolen.
  let axis: "x" | "y" | null = null;
  // relayout() parks the row on an anchor; under a finger that teleports it.
  let relayoutPending = false;
  // A coordinate check can't do this: a drag that loops back ends where it
  // started, and a keyboard click reports x=0 regardless.
  let swallowClick = false;
  let clickGuard: number | null = null;
  let startX = 0;
  let startY = 0;
  let lastX = 0;
  let lastT = 0;
  let velocity = 0;

  const prev = {
    overflow: element.style.overflow,
    cursor: element.style.cursor,
    touchAction: element.style.touchAction,
    userSelect: element.style.userSelect,
    rowDisplay: row.style.display,
    rowFlexWrap: row.style.flexWrap,
    rowAlignItems: row.style.alignItems,
  };
  const prevItem = items.map((item) => ({
    flexShrink: item.style.flexShrink,
    transform: item.style.transform,
    willChange: item.style.willChange,
  }));
  const prevFillHeight = fillTargets.map((node) => node.style.height);

  /* Layout */

  function render() {
    if (loopWidth <= 0) {
      for (const node of moving) node.style.transform = "";
      return;
    }

    let x = ((pos.x % loopWidth) + loopWidth) % loopWidth;
    if (x > 0) x -= loopWidth;

    for (let i = 0; i < moving.length; i++) {
      const offset = bases[i] + x + widths[i] / 2 - viewportCenter;
      const fade = Math.min(1, Math.abs(offset) / focusRange);
      const scale = 1 - (1 - inactiveScale) * fade;
      moving[i].style.transform =
        `translate3d(${x.toFixed(2)}px, 0, 0) scale(${scale.toFixed(4)})`;
    }
  }

  function normalize() {
    if (loopWidth <= 0) return;
    pos.x = ((pos.x % loopWidth) + loopWidth) % loopWidth;
    if (pos.x > 0) pos.x -= loopWidth;
  }

  function measure() {
    for (const clone of clones) clone.remove();
    clones.length = 0;
    // The inset below comes off a rect, so read the row untransformed.
    for (const item of items) item.style.transform = "";

    const first = items[0];
    const last = items[items.length - 1];
    // Misses the trailing gap, which only makes the clone count generous.
    const rough = last.offsetLeft - first.offsetLeft + last.offsetWidth;
    // Zero while hidden, or before images without intrinsic size load. Stays
    // inert until a re-measure finds real numbers.
    if (rough <= 0) {
      loopWidth = 0;
      anchors = items.map(() => 0);
      bases = [];
      widths = [];
      moving = [...items];
      return;
    }

    // A spare cycle past the viewport, so a fast throw can't expose the seam.
    const cycles = Math.ceil(element.clientWidth / rough) + 1;
    for (let c = 0; c < cycles; c++) {
      for (const item of items) {
        const clone = item.cloneNode(true) as HTMLElement;
        clone.setAttribute("aria-hidden", "true");
        clone.style.flexShrink = "0";
        clone.style.willChange = "transform";
        // Including the clone's own root: a card authored as a link block is
        // itself focusable, and aria-hidden plus tabbable strands a reader.
        if (clone.matches(FOCUSABLE)) clone.setAttribute("tabindex", "-1");
        clone
          .querySelectorAll(FOCUSABLE)
          .forEach((el) => el.setAttribute("tabindex", "-1"));
        row.appendChild(clone);
        clones.push(clone);
      }
    }

    // The first clone sits exactly one cycle on, giving the true repeat pitch
    // rather than one reconstructed from gaps.
    const origin = first.offsetLeft;
    loopWidth = clones[0].offsetLeft - origin;
    moving = [...items, ...clones];
    bases = moving.map((node) => node.offsetLeft - origin);
    widths = moving.map((node) => node.offsetWidth);
    // Positions count from the first card but the centre belongs to the
    // element, so the gap between them comes off. It cancels to zero when the
    // row is flush, which is how padding on the root would otherwise hide.
    const inset =
      first.getBoundingClientRect().left -
      element.getBoundingClientRect().left -
      element.clientLeft;
    viewportCenter = element.clientWidth / 2 - inset;
    focusRange = loopWidth / items.length;
    anchors = items.map((item, i) => viewportCenter - bases[i] - widths[i] / 2);
  }

  // Re-measure and park on the card that was showing. A tween holds the start
  // and end it was built from, so it has to die or it drags the row back to
  // the old geometry and every later step inherits that error.
  function relayout() {
    if (destroyed) return;
    if (pointerId !== null) {
      relayoutPending = true;
      return;
    }
    tween?.kill();
    tween = null;
    timer?.kill();
    timer = null;

    const held = index;
    measure();
    index = Math.min(held, anchors.length - 1);
    pos.x = anchors[index];
    render();
    scheduleStep();
  }

  // Travel from card i's rest to the next. Past the last, the next anchor is
  // the first one a cycle on.
  function pitchAfter(i: number) {
    const next =
      i < anchors.length - 1 ? anchors[i + 1] : anchors[0] - loopWidth;
    return anchors[i] - next;
  }

  // Where `index` rests, in the same cycle pos.x is in.
  function restX() {
    return pos.x - symmetricMod(pos.x - anchors[index], loopWidth);
  }

  // Nearest rest to `value`, the short way round.
  function nearest(value: number) {
    if (loopWidth <= 0) return { index: 0, x: value };
    let bestIndex = 0;
    let bestDelta = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < anchors.length; i++) {
      const delta = symmetricMod(anchors[i] - value, loopWidth);
      const distance = Math.abs(delta);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestDelta = delta;
        bestIndex = i;
      }
    }
    return { index: bestIndex, x: value + bestDelta };
  }

  /* Motion */

  function animate(target: number, duration: number, onDone?: () => void) {
    tween?.kill();
    tween = null;

    if (reduced || duration <= 0) {
      pos.x = target;
      normalize();
      render();
      onDone?.();
      return;
    }

    tween = gsap.to(pos, {
      x: target,
      duration,
      ease: EASE,
      onUpdate: render,
      onComplete: () => {
        tween = null;
        normalize();
        render();
        onDone?.();
      },
    });
  }

  function scheduleStep() {
    timer?.kill();
    timer = null;
    if (reduced || destroyed || !inView || loopWidth <= 0) return;
    // A held pointer counts before it becomes a drag: a step firing under a
    // finger fights it for the tween's whole run.
    if (dragging || pointerId !== null) return;
    timer = gsap.delayedCall(interval, step);
  }

  function step() {
    const count = anchors.length;
    let target: number;
    if (direction < 0) {
      target = pos.x - pitchAfter(index);
      index = (index + 1) % count;
    } else {
      index = (index - 1 + count) % count;
      target = pos.x + pitchAfter(index);
    }
    animate(target, stepDuration, scheduleStep);
  }

  /* Drag */

  function onPointerDown(event: PointerEvent) {
    if (pointerId !== null || loopWidth <= 0) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    // Only the countdown stops here. A step in flight survives until the
    // gesture proves itself a drag, since this may yet be a page scroll.
    timer?.kill();
    timer = null;

    pointerId = event.pointerId;
    startX = lastX = event.clientX;
    startY = event.clientY;
    lastT = performance.now();
    velocity = 0;
    dragging = false;
    axis = null;
    swallowClick = false;

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
  }

  function onPointerMove(event: PointerEvent) {
    if (event.pointerId !== pointerId) return;

    if (axis === null) {
      const dx = Math.abs(event.clientX - startX);
      const dy = Math.abs(event.clientY - startY);
      if (dx < DRAG_THRESHOLD && dy < DRAG_THRESHOLD) {
        lastX = event.clientX;
        lastT = performance.now();
        return;
      }
      // Vertical wins ties: a swipe that drifts sideways would otherwise move
      // the row, then throw it when the browser takes the gesture back.
      axis = dx > dy ? "x" : "y";
      if (axis !== "x") {
        endDrag();
        return;
      }

      // Ours now. Re-read the nearest card, since the step died part-way.
      tween?.kill();
      tween = null;
      index = nearest(pos.x).index;

      try {
        element.setPointerCapture(event.pointerId);
      } catch {
        // Best effort; the window listeners carry the gesture anyway.
      }
      dragging = true;
      swallowClick = true;
      element.style.cursor = "grabbing";
      lastX = event.clientX;
      lastT = performance.now();
    }

    const now = performance.now();
    const dt = Math.max(1, now - lastT);
    const dx = event.clientX - lastX;

    pos.x += dx;
    const instant = (dx / dt) * 1000;
    const weight = Math.min(1, dt / VELOCITY_EMA_MS);
    velocity = velocity * (1 - weight) + instant * weight;
    lastX = event.clientX;
    lastT = now;

    render();
  }

  function onPointerUp(event: PointerEvent) {
    if (event.pointerId !== pointerId) return;
    endDrag();
  }

  function endDrag() {
    if (pointerId === null) return;

    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerUp);
    if (element.hasPointerCapture(pointerId)) {
      element.releasePointerCapture(pointerId);
    }
    pointerId = null;
    element.style.cursor = "grab";

    const tookOver = axis === "x";
    dragging = false;
    axis = null;

    // Disarmed after the click has had its turn: a touch drag synthesises no
    // click, and one released off the element sends its click elsewhere.
    if (swallowClick) {
      if (clickGuard !== null) window.clearTimeout(clickGuard);
      clickGuard = window.setTimeout(() => {
        clickGuard = null;
        swallowClick = false;
      }, 0);
    }

    if (!tookOver) {
      // A tap, or a swipe the page kept. A step still running re-arms itself;
      // otherwise land on a card and record which, or index and position drift
      // apart and every later step inherits the gap.
      if (!tween) {
        const settle = nearest(pos.x);
        index = settle.index;
        if (Math.abs(settle.x - pos.x) >= 0.5) {
          animate(settle.x, SETTLE_DURATION, scheduleStep);
          return;
        }
        scheduleStep();
      }
      if (relayoutPending) {
        relayoutPending = false;
        relayout();
      }
      return;
    }

    // Holding still emits no moves, so the last flick's reading would survive
    // the pause and throw the row. Age it as a move would.
    const idle = Math.max(0, performance.now() - lastT);
    velocity *= 1 - Math.min(1, idle / VELOCITY_EMA_MS);

    const count = anchors.length;
    const projected = pos.x + velocity * THROW_PROJECTION;
    let landing = nearest(projected);

    if (landing.index === index) {
      // Short of the halfway line, but a flick past the threshold is a
      // deliberate ask for the next card.
      const thrown = symmetricMod(projected - anchors[index], loopWidth);
      const forward = thrown < 0;
      const from = forward ? index : (index - 1 + count) % count;
      const pitch = pitchAfter(from);
      if (Math.abs(thrown) >= SNAP_THRESHOLD * pitch) {
        landing = forward
          ? { index: (index + 1) % count, x: restX() - pitch }
          : { index: from, x: restX() + pitch };
      }
    }

    index = landing.index;
    velocity = 0;

    if (relayoutPending) {
      relayoutPending = false;
      relayout();
      return;
    }

    animate(landing.x, SETTLE_DURATION, scheduleStep);
  }

  function onLostCapture() {
    endDrag();
  }

  function onClickCapture(event: MouseEvent) {
    if (!swallowClick) return;
    swallowClick = false;
    if (clickGuard !== null) {
      window.clearTimeout(clickGuard);
      clickGuard = null;
    }
    event.preventDefault();
    event.stopPropagation();
  }

  // Images and links are natively draggable; the browser would start its own
  // drag and swallow the gesture behind a ghost image.
  function onDragStart(event: Event) {
    event.preventDefault();
  }

  /* Lifecycle */

  onMount(() => {
    if (handleEditor()) return;

    element.style.overflow = "hidden";
    element.style.cursor = "grab";
    // Vertical panning stays with the page; horizontal is ours.
    element.style.touchAction = "pan-y";
    element.style.userSelect = "none";

    row.style.display = "flex";
    row.style.flexWrap = "nowrap";
    // Asserted, not assumed: a row centred or top-aligned by the design would
    // hand every card its own content height.
    row.style.alignItems = "stretch";
    for (const item of items) {
      item.style.flexShrink = "0";
      item.style.willChange = "transform";
    }
    // Before measure(), so the clones carry the fill.
    for (const target of fillTargets) target.style.height = "100%";

    measure();
    index = 0;
    pos.x = anchors[0];
    render();

    element.addEventListener("pointerdown", onPointerDown);
    element.addEventListener("lostpointercapture", onLostCapture);
    element.addEventListener("click", onClickCapture, { capture: true });
    element.addEventListener("dragstart", onDragStart);

    const offResize = Resize.add(relayout);

    // Everything is measured from the cards, so watching only the root would
    // miss a font swap or an image load that reflows them without moving it.
    const sizeKey = () =>
      `${element.offsetWidth}x${element.offsetHeight}|` +
      items.map((item) => item.offsetWidth).join(",");
    let lastKey = sizeKey();
    const sizeObserver = new ResizeObserver(() => {
      if (destroyed) return;
      const key = sizeKey();
      if (key === lastKey) return;
      lastKey = key;
      if (element.offsetWidth === 0 && element.offsetHeight === 0) return;
      relayout();
      // relayout() writes heights and clones, moving the box just measured.
      lastKey = sizeKey();
    });
    sizeObserver.observe(element);
    for (const item of items) sizeObserver.observe(item);

    onView(element, {
      rootMargin: VIEW_MARGIN,
      autoStart: true,
      callback: ({ isIn }) => {
        inView = isIn;
        if (!isIn) {
          timer?.kill();
          timer = null;
          return;
        }
        // First time on screen is the first chance a section that mounted
        // hidden gets to measure itself.
        if (loopWidth <= 0) relayout();
        else scheduleStep();
      },
    });

    onDestroy(() => {
      destroyed = true;
      offResize();
      sizeObserver.disconnect();
      endDrag();
      if (clickGuard !== null) {
        window.clearTimeout(clickGuard);
        clickGuard = null;
      }
      swallowClick = false;
      tween?.kill();
      tween = null;
      timer?.kill();
      timer = null;

      element.removeEventListener("pointerdown", onPointerDown);
      element.removeEventListener("lostpointercapture", onLostCapture);
      element.removeEventListener("click", onClickCapture, { capture: true });
      element.removeEventListener("dragstart", onDragStart);

      for (const clone of clones) clone.remove();
      clones.length = 0;
      moving = [];

      items.forEach((item, i) => {
        item.style.flexShrink = prevItem[i].flexShrink;
        item.style.transform = prevItem[i].transform;
        item.style.willChange = prevItem[i].willChange;
      });
      fillTargets.forEach((node, i) => {
        node.style.height = prevFillHeight[i];
      });

      element.style.overflow = prev.overflow;
      element.style.cursor = prev.cursor;
      element.style.touchAction = prev.touchAction;
      element.style.userSelect = prev.userSelect;
      row.style.display = prev.rowDisplay;
      row.style.flexWrap = prev.rowFlexWrap;
      row.style.alignItems = prev.rowAlignItems;
    });
  });
}
